/**
 * Where you are, and what you can do from here.
 *
 * The model underneath a scan is a *checkpoint*: a coded door or rack whose type
 * (STORE_OUT, GATE_IN, …) is what makes the server change a mould's status and
 * custody. That is the right model and the wrong thing to put in front of a
 * person. Nobody on a shop floor thinks "I am at CHK-MIRA-STORE-OUT"; they think
 * "I am in the Mira Road store and I am taking this out".
 *
 * So this module inverts it. The scanner asks for a *place* - a site, and a store
 * within it if there is one - and derives which checkpoint each action needs.
 * Check out, check in and log-where-it-is become three buttons, and the codes
 * stay where they belong, in the database.
 *
 * When a place cannot do something, that is said here rather than discovered on
 * submit. A site with no check-out point should grey nothing out silently; it
 * should say "Mira Road has no check-out point set up" while there is still
 * somebody standing there who can go and ask for one.
 */
import { useEffect, useState } from 'react';
import type { CheckpointOption, CheckpointType } from '@mouldtrack/shared';
import { isNetworkError, listCheckpoints } from '../../api/client.js';

const CACHE_KEY = 'mt_checkpoints';
const PLACE_KEY = 'mt_scan_place';

/** What a person can do with a mould in their hands. */
export type ScanAction = 'out' | 'in' | 'here';

export const ACTION_LABEL: Record<ScanAction, string> = {
  out: 'Check out',
  in: 'Check in',
  here: 'Log location',
};

export const ACTION_HINT: Record<ScanAction, string> = {
  out: 'Taking it out - it goes into your name until it comes back',
  in: 'Putting it back - it stops being held by anyone',
  here: 'Just recording where it is, without changing who holds it',
};

/**
 * Which checkpoint types serve which action.
 *
 * Ordered by preference. A store has its own in/out points; a site as a whole
 * has gates. Looking for the store's first means someone standing at a rack
 * records a rack movement rather than a site-boundary crossing, which is the
 * difference between "it is on shelf B" and "it left the premises".
 */
const TYPES_FOR: Record<ScanAction, readonly CheckpointType[]> = {
  out: ['STORE_OUT', 'GATE_OUT'],
  in: ['STORE_IN', 'GATE_IN'],
  here: ['LOCATION', 'STORE_IN', 'GATE_IN'],
};

export interface Place {
  locationId: string;
  storeId: string | null;
}

export interface PlaceOption {
  locationId: string;
  locationName: string;
  storeId: string | null;
  storeName: string | null;
  /** "Mira Road" or "Mira Road · Rack A1" - what the picker shows. */
  label: string;
}

export interface ResolvedAction {
  action: ScanAction;
  label: string;
  hint: string;
  /** The checkpoint this action would scan at, or null when the place has none. */
  checkpoint: CheckpointOption | null;
  /** Why it is unavailable, in words a supervisor can act on. */
  unavailable: string | null;
}

// --- checkpoints, cached so the scanner survives a dead zone ---

function readCache(): CheckpointOption[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? (parsed as CheckpointOption[]) : [];
  } catch {
    return []; // a corrupt cache is an empty cache, never a crash
  }
}

export interface CheckpointsState {
  checkpoints: CheckpointOption[];
  loading: boolean;
  /** True when the list on screen came from the cache rather than the server. */
  stale: boolean;
}

/**
 * Every checkpoint this account may scan at.
 *
 * Fetched, not read from Dexie, because the replicated `checkpoints` table holds
 * `locationId` and `storeId` and the scanner needs the *names* behind them. The
 * result is cached, so the second time someone opens the scanner in a store room
 * with no signal it still knows where they might be standing.
 */
export function useCheckpoints(): CheckpointsState {
  const [checkpoints, setCheckpoints] = useState<CheckpointOption[]>(readCache);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      setLoading(true);
      listCheckpoints()
        .then((rows) => {
          if (cancelled) return;
          setCheckpoints(rows);
          setStale(false);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(rows));
          } catch {
            // Quota or private mode: this session still has the list in memory.
          }
        })
        .catch((error: unknown) => {
          // Offline is not an error here - the cache is the answer. Anything
          // else leaves the cached list up too, because a scanner that shows
          // nowhere to stand is worse than one showing yesterday's doors.
          if (!cancelled && !isNetworkError(error)) setStale(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    load();
    window.addEventListener('online', load);
    return () => {
      cancelled = true;
      window.removeEventListener('online', load);
    };
  }, []);

  return { checkpoints, loading, stale };
}

// --- places ---

/** Every site and store the checkpoints imply, as one flat picker list. */
export function placeOptions(checkpoints: CheckpointOption[]): PlaceOption[] {
  const seen = new Map<string, PlaceOption>();

  for (const cp of checkpoints) {
    const locationName = cp.location?.name ?? cp.location?.code ?? 'Unnamed site';
    // A site entry for every location, whether or not it has stores: gates and
    // plain location scans hang off the site itself.
    const siteKey = `${cp.locationId}:`;
    if (!seen.has(siteKey)) {
      seen.set(siteKey, {
        locationId: cp.locationId,
        locationName,
        storeId: null,
        storeName: null,
        label: locationName,
      });
    }

    if (cp.storeId) {
      const storeName = cp.store?.name ?? cp.store?.code ?? 'Unnamed store';
      const storeKey = `${cp.locationId}:${cp.storeId}`;
      if (!seen.has(storeKey)) {
        seen.set(storeKey, {
          locationId: cp.locationId,
          locationName,
          storeId: cp.storeId,
          storeName,
          label: `${locationName} · ${storeName}`,
        });
      }
    }
  }

  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The three actions, resolved against where the person says they are.
 *
 * Always returns all three. An action with no checkpoint behind it still
 * appears, carrying the reason - because "Mira Road has no check-out point"
 * tells an administrator what to create, and a missing button tells them
 * nothing at all.
 */
export function actionsFor(
  checkpoints: CheckpointOption[],
  place: Place | null,
): ResolvedAction[] {
  return (Object.keys(ACTION_LABEL) as ScanAction[]).map((action) => {
    const base = { action, label: ACTION_LABEL[action], hint: ACTION_HINT[action] };
    if (!place) {
      return { ...base, checkpoint: null, unavailable: 'Choose where you are first.' };
    }

    const here = checkpoints.filter((cp) => cp.locationId === place.locationId);
    const siteName = here[0]?.location?.name ?? 'this site';

    for (const type of TYPES_FOR[action]) {
      // A store was named, so prefer that store's own point; fall back to one
      // that serves the whole site, which is what a gate is.
      const match =
        (place.storeId ? here.find((cp) => cp.storeId === place.storeId && cp.type === type) : null) ??
        here.find((cp) => cp.storeId === null && cp.type === type) ??
        (place.storeId ? null : here.find((cp) => cp.type === type));
      if (match) return { ...base, checkpoint: match, unavailable: null };
    }

    return {
      ...base,
      checkpoint: null,
      unavailable:
        action === 'here'
          ? `${siteName} has no scan point set up.`
          : `${siteName} has no ${ACTION_LABEL[action].toLowerCase()} point set up. An administrator can add one under Sites & Scanning → Checkpoints.`,
    };
  });
}

// --- the remembered place ---

/**
 * Where this device was last used.
 *
 * A supervisor stands in the same store all week. Asking them to pick the site
 * again on every scan is the kind of friction that ends with everyone picking
 * whatever is first in the list, which quietly poisons the data the product
 * exists to produce.
 */
export function readPlace(): Place | null {
  try {
    const raw = localStorage.getItem(PLACE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return null;
    const place = parsed as Place;
    return typeof place.locationId === 'string' ? place : null;
  } catch {
    return null;
  }
}

export function writePlace(place: Place | null): void {
  try {
    if (place) localStorage.setItem(PLACE_KEY, JSON.stringify(place));
    else localStorage.removeItem(PLACE_KEY);
  } catch {
    // Not being able to remember the place is a small loss, never a failure.
  }
}

/** Serialise a place for the picker's `value`, and back again. */
export const placeKey = (p: Place | null): string => (p ? `${p.locationId}:${p.storeId ?? ''}` : '');

export function parsePlaceKey(key: string): Place | null {
  if (!key) return null;
  const [locationId, storeId] = key.split(':');
  if (!locationId) return null;
  return { locationId, storeId: storeId || null };
}
